import { WEBPORTAL_REACT_PROFILE_DIALOG_FLAG } from '../components/profile/profileFeatureFlags';

export type ProfileHostUser = Record<string, unknown> | null | undefined;
export type ProfileHostBranch = Record<string, unknown> | null | undefined;

export type ProfileHostModalOptions = {
  templateUrl?: string;
  controller?: string;
  controllerAs?: string;
  inputs?: Record<string, unknown>;
};

/**
 * Angular-owned ports for a future `useProfile` hook. Signatures mirror what
 * `ProfileController` calls today; HTTP stays in the Angular factories.
 */
export type ProfileHostDependencies = {
  getCurrentUser: () => ProfileHostUser;
  getCurrentBranch: () => ProfileHostBranch;
  isFeatureFlagEnabled: (name: string) => boolean;
  modifyProfile: (userId: string, changes: unknown, image?: File | Blob | null) => Promise<unknown>;
  getUserConsentObjectUpdated: (consents: unknown) => unknown;
  getTransactionsById: (userId: string, page?: number) => Promise<unknown>;
  getDocumentsByUserId: (branchId: string, userId: string) => Promise<unknown>;
  getBranchDocuments: (branchId: string) => Promise<unknown>;
  getBookingsById: (userId: string, includeSpotBookings?: boolean) => Promise<unknown>;
  getLinkedAccount: (userId: string) => Promise<unknown>;
  getUsersStackedMemberships: (branchId: string, userId: string) => Promise<unknown>;
  cancelBookingById: (userId: string, bookingId: string) => Promise<unknown>;
  buyMembershipPlan: (...args: unknown[]) => Promise<unknown>;
  logOut: () => void | Promise<unknown>;
  showModal: (options: ProfileHostModalOptions) => Promise<unknown>;
  triggerAnalytics: (event: Record<string, unknown>) => void;
  onUserChange: (callback: () => void) => () => void;
  onFeatureFlagsLoaded: (callback: () => void) => () => void;
  getCancelRequestIds: () => unknown;
  setCancelRequestIds: (value: unknown) => void;
};

export type ProfileHostPorts = {
  flagName: typeof WEBPORTAL_REACT_PROFILE_DIALOG_FLAG;
  isReactProfileDialogEnabled: () => boolean;
  getCurrentUser: () => ProfileHostUser;
  getCurrentBranch: () => ProfileHostBranch;
  modifyProfile: ProfileHostDependencies['modifyProfile'];
  getUserConsentObjectUpdated: ProfileHostDependencies['getUserConsentObjectUpdated'];
  getTransactionsById: ProfileHostDependencies['getTransactionsById'];
  getDocumentsByUserId: ProfileHostDependencies['getDocumentsByUserId'];
  getBranchDocuments: ProfileHostDependencies['getBranchDocuments'];
  getBookingsById: ProfileHostDependencies['getBookingsById'];
  getLinkedAccount: ProfileHostDependencies['getLinkedAccount'];
  getUsersStackedMemberships: ProfileHostDependencies['getUsersStackedMemberships'];
  cancelBookingById: ProfileHostDependencies['cancelBookingById'];
  buyMembershipPlan: ProfileHostDependencies['buyMembershipPlan'];
  logOut: ProfileHostDependencies['logOut'];
  showModal: ProfileHostDependencies['showModal'];
  triggerAnalytics: ProfileHostDependencies['triggerAnalytics'];
  onUserChange: ProfileHostDependencies['onUserChange'];
  onFeatureFlagsLoaded: ProfileHostDependencies['onFeatureFlagsLoaded'];
  getCancelRequestIds: ProfileHostDependencies['getCancelRequestIds'];
  setCancelRequestIds: ProfileHostDependencies['setCancelRequestIds'];
};

/** Pass-through host (RNA-5278). Unused by ProfileController until orchestration. */
export function createProfileHost(dependencies: ProfileHostDependencies): ProfileHostPorts {
  return {
    flagName: WEBPORTAL_REACT_PROFILE_DIALOG_FLAG,
    isReactProfileDialogEnabled: () =>
      dependencies.isFeatureFlagEnabled(WEBPORTAL_REACT_PROFILE_DIALOG_FLAG),
    getCurrentUser: () => dependencies.getCurrentUser(),
    getCurrentBranch: () => dependencies.getCurrentBranch(),
    modifyProfile: (userId, changes, image) => dependencies.modifyProfile(userId, changes, image),
    getUserConsentObjectUpdated: (consents) => dependencies.getUserConsentObjectUpdated(consents),
    getTransactionsById: (userId, page) => dependencies.getTransactionsById(userId, page),
    getDocumentsByUserId: (branchId, userId) => dependencies.getDocumentsByUserId(branchId, userId),
    getBranchDocuments: (branchId) => dependencies.getBranchDocuments(branchId),
    getBookingsById: (userId, includeSpotBookings) =>
      dependencies.getBookingsById(userId, includeSpotBookings),
    getLinkedAccount: (userId) => dependencies.getLinkedAccount(userId),
    getUsersStackedMemberships: (branchId, userId) =>
      dependencies.getUsersStackedMemberships(branchId, userId),
    cancelBookingById: (userId, bookingId) => dependencies.cancelBookingById(userId, bookingId),
    buyMembershipPlan: (...args) => dependencies.buyMembershipPlan(...args),
    logOut: () => dependencies.logOut(),
    showModal: (options) => dependencies.showModal(options),
    triggerAnalytics: (event) => dependencies.triggerAnalytics(event),
    onUserChange: (callback) => dependencies.onUserChange(callback),
    onFeatureFlagsLoaded: (callback) => dependencies.onFeatureFlagsLoaded(callback),
    getCancelRequestIds: () => dependencies.getCancelRequestIds(),
    setCancelRequestIds: (value) => dependencies.setCancelRequestIds(value),
  };
}
